import { memo } from "react";
import { PaginationControls } from "@/components/ui/PaginationControls/PaginationControls";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import {
  GSC_PAGE_SIZES,
  isGscPageSize,
  type GscPageSize,
} from "@/types/gsc";

interface DetailTablePaginationProps {
  page: number;
  pageSize: GscPageSize;
  totalCount: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: GscPageSize) => void;
  className?: string;
}

function DetailTablePaginationComponent({
  page,
  pageSize,
  totalCount,
  onPageChange,
  onPageSizeChange,
  className,
}: DetailTablePaginationProps) {
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const rangeStart = totalCount === 0 ? 0 : (page - 1) * pageSize + 1;
  const rangeEnd = Math.min(page * pageSize, totalCount);

  const handlePageSizeChange = (value: string): void => {
    const next = Number(value);
    if (!isGscPageSize(next)) return;
    onPageSizeChange(next);
  };

  return (
    <div
      className={cn(
        "mt-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between",
        className,
      )}
    >
      <div className="flex flex-wrap items-center gap-2 px-1">
        <span className="text-sm text-muted-foreground tabular-nums">
          {rangeStart.toLocaleString()}–{rangeEnd.toLocaleString()} of{" "}
          {totalCount.toLocaleString()}
        </span>
        <span className="text-sm text-muted-foreground">·</span>
        <span className="text-sm text-muted-foreground">Show</span>
        <Select
          value={String(pageSize)}
          onValueChange={handlePageSizeChange}
        >
          <SelectTrigger className="h-9 w-[80px]" aria-label="Rows per page">
            <SelectValue placeholder="15" />
          </SelectTrigger>
          <SelectContent>
            {GSC_PAGE_SIZES.map((size) => (
              <SelectItem key={size} value={String(size)}>
                {size}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <span className="text-sm text-muted-foreground">rows</span>
      </div>

      {totalPages > 1 ? (
        <PaginationControls
          currentPage={page}
          totalPages={totalPages}
          onPageChange={onPageChange}
        />
      ) : null}
    </div>
  );
}

export const DetailTablePagination = memo(DetailTablePaginationComponent);
DetailTablePagination.displayName = "DetailTablePagination";
